import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IconName } from "capitalrx-components-2.0/lib/general";
import { Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";

import { DesktopMenuBar } from "@/App/components/MenuBar/DesktopMenuBar";
import { SearchParamKeys } from "@/App/constants/constants";
import { ExperienceContext, MemberEligibilityContext } from "@/App/context";
import { useIsDiscountUser, useIsMedicareUser, useIsUnifiedUser } from "@/App/hooks";
import { useListCategorizedExternalConnectionList } from "@/App/hooks/externalConnections";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service";
import { AppMenuItem, MenuLocation } from "@/App/types/experience";
import { enableDMR, enableNewAccums } from "@/App/utils/featureFlags";

import { MobileMainBar, MobileMenuDrawer, MobileTopBar } from "./MobileMenuBar";

export interface MenuItem {
  readonly icon?: IconName;
  readonly to: string;
  readonly displayText: string;
  readonly openBehavior?: "newTab" | "externalPage";
  readonly analyticsEvent?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly analyticsParams?: any;
}

const TRANSLATION_PATH = "MENU_BAR";
export const MenuBar = () => {
  const { t: translate } = useTranslation();

  const {
    experience,
    hideFindBestPricePage,
    hideMemberClaimsPage,
    hideFormularyLookup,
    formularyLookupOverrideUrl,
    hideNearbyPharmaciesPage,
    hidePriorAuthorization,
    hideEligibility,
    overrideLandingPage,
    hideDashboard,
    hideJudiCareOutboundSSO,
    hideMedicarePrescriptionPaymentPlan,
    hideDirectMemberReimbursement,
    hideAccumulators,
  } = useContext(ExperienceContext);
  const { isUnifiedUser } = useIsUnifiedUser();
  const { isMedicareUser, isPartD } = useIsMedicareUser();
  const { isDiscountUser } = useIsDiscountUser();
  const hideCareUsageLimits = !isUnifiedUser;
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { externalConnectionsGeneral, externalConnectionsJudiCare } = useListCategorizedExternalConnectionList({
    location: window.location.href,
  });
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const isEmulating = !!sessionStorage.getItem("isEmulation");

  const isDMRPermitted =
    memberEligibility.plan_benefit.direct_member_reimbursement === "yes"
    || (memberEligibility.plan_benefit.direct_member_reimbursement == null && isMedicareUser);

  const { isMobile } = useResponsive({
    isMobile: {
      mobile: true,
      desktop: false,
    },
  });

  useEffect(() => {
    if (!isMobile) {
      setMobileDrawerVisible(false);
    }
  }, [isMobile]);

  const onMobileMenuToggle = useCallback(() => {
    const visible = !mobileDrawerVisible;
    // top menu content is rendered unconditionally at the top of the display, so make sure the menu content we're overlaying is at the top too
    if (visible) {
      window.scrollTo(0, 0);
    }
    setMobileDrawerVisible(visible);
    logEvent(ANALYTIC_EVENTS.DESKTOP_SIDE_NAV);
  }, [mobileDrawerVisible]);

  const mappedMenuItems = useMemo(() => {
    const menuItems: { [key in MenuLocation]: AppMenuItem[] } = {
      care_search: [],
      claims_coverage: [],
      custom: [],
    };
    experience?.menu_items?.forEach((menuItem) => menuItems[menuItem["menu_location"]].push(menuItem));
    return menuItems;
  }, [experience?.menu_items]);

  const careSearchMenuItems = useMemo(() => {
    const careSearch: MenuItem[] = [];
    if (!hideFindBestPricePage) {
      careSearch.push({
        to: "/find-best-price",
        displayText: translate(`${TRANSLATION_PATH}.findBestPrice`),
        analyticsEvent: ANALYTIC_EVENTS.FIND_BEST_PRICE.topNav,
      });
    }
    if (!hideFormularyLookup) {
      careSearch.push({
        displayText: translate(`${TRANSLATION_PATH}.lookupFormulary`),
        openBehavior: "externalPage",
        to: formularyLookupOverrideUrl || memberEligibility?.formulary?.lookup_tool_url,
        analyticsEvent: ANALYTIC_EVENTS.FORMULARY.topNav,
      });
    }
    if (!hideNearbyPharmaciesPage) {
      careSearch.push({
        to: "/nearby-pharmacy",
        displayText: translate(`${TRANSLATION_PATH}.nearbyPharmacies`),
        analyticsEvent: ANALYTIC_EVENTS.NEARBY_PHARMACIES.topNav,
      });
    }
    if (externalConnectionsJudiCare?.length > 0 && !hideJudiCareOutboundSSO) {
      const urlParams = new URLSearchParams();
      if (isViewingDataholder) {
        urlParams.set(SearchParamKeys.MEMBER_ELIGIBILITY_MEMBER_ID, memberEligibility.member.id.toString());
      }
      const extConn: MenuItem[] = externalConnectionsJudiCare?.map((connection) => {
        const queryString = isViewingDataholder ? `?${urlParams.toString()}` : "";
        const toValue = `${location.protocol}//${location.host}/connection/${connection.link.id}${queryString}`;
        return {
          displayText: translate(`${TRANSLATION_PATH}.careNavigation`),
          to: toValue,
          openBehavior: "externalPage",
          analyticsEvent: ANALYTIC_EVENTS.EXTERNAL_CONNECTIONS.topNav,
        };
      });
      careSearch.push(...extConn);
    }
    if (mappedMenuItems?.care_search?.length > 0) {
      mappedMenuItems?.care_search?.forEach((menuItem) => {
        careSearch.push({
          to: menuItem.url,
          displayText: menuItem.display_text,
          analyticsEvent: ANALYTIC_EVENTS.NEARBY_PHARMACIES.topNav,
          openBehavior: "externalPage",
        });
      });
    }
    return careSearch;
  }, [externalConnectionsJudiCare, hideFindBestPricePage, hideFormularyLookup, hideNearbyPharmaciesPage, mappedMenuItems]);

  const claimsAndCoverageMenuItems = useMemo(() => {
    const claimsAndCoverage: MenuItem[] = [];
    if (!hideMemberClaimsPage) {
      claimsAndCoverage.push({
        displayText: translate(`${TRANSLATION_PATH}.claimsHistory`),
        to: "/claims-history",
        analyticsEvent: ANALYTIC_EVENTS.CLAIM_HISTORY.topNav,
      });
    }
    if (!hideEligibility) {
      claimsAndCoverage.push({
        displayText: translate(`${TRANSLATION_PATH}.eligibility`),
        to: "/eligibility",
        analyticsEvent: ANALYTIC_EVENTS.ELIGIBILITY.topNav,
      });
    }
    if (!hidePriorAuthorization) {
      claimsAndCoverage.push({
        displayText: translate(`${TRANSLATION_PATH}.priorAuthorization`),
        to: "/prior-authorization",
        analyticsEvent: ANALYTIC_EVENTS.PRIOR_AUTHORIZATION.topNav,
      });
    }
    if (!hideCareUsageLimits) {
      claimsAndCoverage.push({
        icon: "medical",
        displayText: translate(`${TRANSLATION_PATH}.careUsageAndLimits`),
        to: "/care-usage-and-limits",
        analyticsEvent: ANALYTIC_EVENTS.CARE_USAGE_AND_LIMITS.topNav,
      });
    }
    if (!hideMedicarePrescriptionPaymentPlan && isPartD) {
      claimsAndCoverage.push({
        displayText: translate(`${TRANSLATION_PATH}.medicarePrescriptionPaymentPlan`),
        to: "/medicare-prescription-payment-plan",
        analyticsEvent: ANALYTIC_EVENTS.MEDICARE_PRESCRIPTION_PAYMENT_PLAN.topNav,
      });
    } if (!isMedicareUser && !isDiscountUser && !hideAccumulators && enableNewAccums()) {
      claimsAndCoverage.push({
        displayText: translate(`${TRANSLATION_PATH}.planContributions`),
        to: "/plan-contributions",
        analyticsEvent: ANALYTIC_EVENTS.PLAN_CONTRIBUTIONS.topNav,
      });
    }
    if (enableDMR() && !isEmulating && !hideDirectMemberReimbursement && isDMRPermitted) {
      claimsAndCoverage.push({
        displayText: translate(`${TRANSLATION_PATH}.dmr`),
        to: "/direct-member-reimbursement",
        analyticsEvent: ANALYTIC_EVENTS.DMR.topNav,
      });
    }
    if (mappedMenuItems?.claims_coverage?.length > 0) {
      mappedMenuItems?.claims_coverage?.forEach((menuItem) => {
        claimsAndCoverage.push({
          to: menuItem.url,
          displayText: menuItem.display_text,
          analyticsEvent: ANALYTIC_EVENTS.NEARBY_PHARMACIES.topNav,
          openBehavior: "externalPage",
        });
      });
    }
    return claimsAndCoverage;
  }, [
    hideMemberClaimsPage,
    hideEligibility,
    hidePriorAuthorization,
    hideMedicarePrescriptionPaymentPlan,
    isPartD,
    hideDirectMemberReimbursement,
    mappedMenuItems,
  ]);

  const dynamicLinkItems: MenuItem[] = mappedMenuItems?.custom?.map((dl) => {
    return {
      to: dl.url,
      openBehavior: "externalPage",
      displayText: dl.display_text,
      openInNewTab: true,
      analyticsEvent: ANALYTIC_EVENTS.APP_MENU_ITEMS.topNav,
    };
  });

  const homeItems = useMemo(() => {
    const home: MenuItem[] = [];
    if (overrideLandingPage) {
      home.push({
        displayText: translate(`${TRANSLATION_PATH}.home`),
        to: overrideLandingPage,
      });
    }
    if (!hideDashboard) {
      home.push({
        displayText: overrideLandingPage ? translate(`${TRANSLATION_PATH}.myRx`) : translate(`${TRANSLATION_PATH}.home`),
        to: "/",
        analyticsEvent: ANALYTIC_EVENTS.MY_RX.topNav,
      });
    }
    return home;
  }, [overrideLandingPage, hideDashboard]);

  const externalConnectionItems = useMemo(() => {
    if (externalConnectionsGeneral?.length > 0) {
      const urlParams = new URLSearchParams();
      if (isViewingDataholder) {
        urlParams.set(SearchParamKeys.MEMBER_ELIGIBILITY_MEMBER_ID, memberEligibility.member.id.toString());
      }
      const extConn: MenuItem[] = externalConnectionsGeneral?.map((connection) => {
        const queryString = isViewingDataholder ? `?${urlParams.toString()}` : "";
        const toValue = `${location.protocol}//${location.host}/connection/${connection.link.id}${queryString}`;
        return {
          displayText: connection.link.label,
          to: toValue,
          analyticsEvent: ANALYTIC_EVENTS.EXTERNAL_CONNECTIONS.topNav,
          openBehavior: "externalPage",
        };
      });
      return extConn;
    }
    return [];
  }, [externalConnectionsGeneral, isViewingDataholder]);

  const onCommonMenuItemClick = useCallback((menuItem: MenuItem) => {
    if (menuItem.analyticsEvent) {
      if (menuItem.analyticsParams) {
        logEvent(menuItem.analyticsEvent, menuItem.analyticsParams);
      } else {
        logEvent(menuItem.analyticsEvent);
      }
    }
  }, []);

  return (
    <Stack spacing="none">
      {!isMobile && (
        <DesktopMenuBar
          dynamicLinkItems={dynamicLinkItems}
          externalConnectionItems={externalConnectionItems}
          claimsAndCoverageMenuItems={claimsAndCoverageMenuItems}
          careSearchMenuItems={careSearchMenuItems}
          onCommonMenuItemClick={onCommonMenuItemClick}
          homeItems={homeItems}
        />
      )}
      {isMobile && (
        <>
          <MobileTopBar />
          <MobileMainBar mobileMenuIcon={mobileDrawerVisible ? "x" : "menu-bars"} onMobileMenuToggle={onMobileMenuToggle} />
          <MobileMenuDrawer
            isVisible={mobileDrawerVisible}
            setVisible={setMobileDrawerVisible}
            homeItems={homeItems}
            dynamicLinkItems={dynamicLinkItems}
            externalConnectionItems={externalConnectionItems}
            claimsAndCoverageMenuItems={claimsAndCoverageMenuItems}
            careSearchMenuItems={careSearchMenuItems}
            onCommonMenuItemClick={onCommonMenuItemClick}
          />
        </>
      )}
    </Stack>
  );
};
